import { Fragment, useEffect, useState } from "react";
import { Col, OverlayTrigger, Row, Tooltip } from "react-bootstrap";
import AbpnSpinner from "../../Layout/Components/AbpnSpinner";
import { getDateWithOffset, getLocaleDateWithOffsetString } from "../../Layout/Components/GetDateWithOffset";
import { LookupItem } from "../../Layout/Components/LookupItem";
import { Certificate, ClinicalStatus, CertificateStatus, CertificationGeneration } from "../Models/Certificate";
import { PhysicianSearchResult } from "../Models/PhysicianSearchResult";

interface IProps {
    certificateGroup: Certificate[];
    physician: PhysicianSearchResult;
}

export function CertificateRow(props: IProps) {
    const [displayCertificate, setDisplayCertificate] = useState<Certificate>(null);
    const [expiredCertificatesByDate, setExpiredCertificatesByDate] = useState<Certificate[]>([]);

    let clinicalStatusOptions: Array<LookupItem> = [];
    for (let key in ClinicalStatus) {
        let value = ClinicalStatus[key];
        if (value === "Unknown" || value === "Did Not Report") continue;
        clinicalStatusOptions.push({ id: parseInt(key), name: value })
    }

    useEffect(() => {
        let activeCertificate = props.certificateGroup.find(_ => _.certificateStatus === CertificateStatus.Active);
        let inactiveCertificatesByDate = props.certificateGroup.filter(_ => _.certificateStatus === CertificateStatus.Inactive).sort(
            (a, b) => getDateWithOffset(b.awardDate).getTime() - getDateWithOffset(a.awardDate).getTime()
        );
        let displayCert = activeCertificate ?? inactiveCertificatesByDate[0];
        setDisplayCertificate(displayCert);
        setExpiredCertificatesByDate(inactiveCertificatesByDate)
    }, []);
    var ccLabel = "Continuing Certification Status";
    var mocLabel = "MOC Status";
    let ccMocStatusLabel: string;

    var showInfoIcon = false;
    var displayEndDateLabel = true;
    var endDateLabel = "";
    var certificateEndDateDisplay: string;

    switch (displayCertificate?.certificationGeneration) {
        case CertificationGeneration.TenYear:
            ccMocStatusLabel = mocLabel;
            certificateEndDateDisplay =  getDateWithOffset(displayCertificate.expirationDate).toLocaleDateString();

            break;
        case CertificationGeneration.Lifetime:
            ccMocStatusLabel = mocLabel;
            if (props.physician.isCurrentBlockBehind != null) {
                ccMocStatusLabel = ccLabel;
            }
            if (displayCertificate.certificateStatus == CertificateStatus.Active) displayEndDateLabel = false;
            break;
        case CertificationGeneration.Cc:
            ccMocStatusLabel = ccLabel;
            if (!displayCertificate.isHPM) showInfoIcon = true;            
            break;
        default:
            break;
    }
    var isActiveCC = displayCertificate?.certificationGeneration === CertificationGeneration.Cc 
                            && displayCertificate.certificateStatus === CertificateStatus.Active;

    if (!isActiveCC && displayCertificate?.expirationDate) {
        showInfoIcon = false;
        endDateLabel = "Expiration";
        certificateEndDateDisplay = getDateWithOffset(displayCertificate.expirationDate).toLocaleDateString();
    }
    
    var infoIcon = (<OverlayTrigger
                placement={"right"}
                delay={{ hide: 800 } as any}
                overlay={<Tooltip>
                    <p>
                        ABPN certificates issued in 2012 and after do not have an end date since ongoing certification is
                        contingent upon a diplomate meeting all ABPN requirements of the Continuing Certification Program.
                    </p>
                </Tooltip>}>
                <i className="las la-info-circle"></i>
            </OverlayTrigger>);

    let displayDescription =
        (displayCertificate?.certificateStatus === CertificateStatus.Active ?
            <div className="vc-font-size-smaller">Certified</div>
            :
            <div className="vc-font-size-smaller text-danger">Not Certified</div>
        );

    if (props.physician.isRetired) {
        if (displayCertificate?.expirationDate) {
            displayEndDateLabel = true;
            endDateLabel = "Retired Notification"
            certificateEndDateDisplay =  getDateWithOffset(displayCertificate.expirationDate).toLocaleDateString();
        }
        showInfoIcon = false;
        displayDescription = <div className="vc-font-size-smaller">Certified - Retired</div>
    }

        
    if (displayCertificate?.isHPM) displayDescription = (<div className="vc-font-size-smaller">ABPN issued certificates in Hospice and Palliative Medicine (HPM) from 2008-2021.  In 2022, HPM certificates issued by ABPN were transitioned to the American Board of Internal Medicine (ABIM) for initial certification and continuing certification.  
                        For the current status of this certificate, please visit<a href="https://www.abim.org/" target="_blank"> ABIM.org</a>.</div>)

    if (!displayCertificate) return <AbpnSpinner /> 
    return (
            <Fragment>
                <Row className="mb-3">
                    <Col className="mb-1" xs={12} md={12} lg={4} xl={4}>
                        <div className="fw-bolder mb-1 vc-font-size-smaller">
                            {displayCertificate.certificationTitle.toUpperCase()}
                        </div>
                        {displayDescription}
                    </Col>
                    <Col xs={12} lg={8}>
                        <Row>
                            <Col xs={12} lg={displayEndDateLabel ? 3 : 6}>
                                <div className="fw-bolder mb-1 vc-font-size-smaller">Awarded
                                    &nbsp;&nbsp;{showInfoIcon && displayCertificate.certificationGeneration == CertificationGeneration.Cc &&
                                        infoIcon
                                    }
                                </div>
                                <div className="vc-font-size-smaller">
                                    {getDateWithOffset(displayCertificate.awardDate).toLocaleDateString()}
                                </div>
                            </Col>
                            {displayEndDateLabel &&
                                <Fragment>
                                    <Col xs={12} lg={3}>
                                        <div className="fw-bolder mb-1 vc-font-size-smaller">{endDateLabel}
                                            
                                        </div>
                                        <div className="vc-font-size-smaller">{certificateEndDateDisplay}</div>
                                    </Col>
                                </Fragment>
                            }
                            <Col xs={12} lg={6}>
                                <div className="fw-bolder mb-1 vc-font-size-smaller">Certificate Number</div>
                                <div className="vc-font-size-smaller">{displayCertificate.certificateNumber}</div>
                            </Col>
                        </Row>

                        {!props.physician.isRetired &&
                        <Fragment>
                            <hr />
                            <Row>
                                <Col xs={12} lg={6}>
                                    <div className="fw-bolder mb-1 vc-font-size-smaller">{ccMocStatusLabel}</div>
                                    <div className="vc-font-size-smaller">{displayCertificate.ccStatusDisplay}</div>
                                </Col>
                            </Row>
                        </Fragment>}
                        
                        {expiredCertificatesByDate.length > 0 &&
                            <Fragment>
                                <hr />
                                <Row>
                                    <Col>
                                        <div className="fw-bolder mb-1 vc-font-size-smaller">Certification History</div>
                                        {displayCertificate.certificateStatus === CertificateStatus.Active &&
                                            <div className="vc-font-size-smaller">Certified on {getLocaleDateWithOffsetString(displayCertificate.awardDate)} | Certificate Number {displayCertificate.certificateNumber}</div>}
                                        {expiredCertificatesByDate.map(_ =>
                                            <div className="vc-font-size-smaller">Certified from {getLocaleDateWithOffsetString(_.awardDate)} to {getLocaleDateWithOffsetString(_.expirationDate)} | Certificate Number {_.certificateNumber}</div>
                                        )}
                                    </Col>
                                </Row>
                            </Fragment>
                        }
                    </Col>
                </Row>
                <hr />
            </Fragment>
    );
}